/**
 * The cruise law (plan §2.2, R6; D-TBD 2, 15). Pure and allocation-free except `simulateTrip`, a tuning and test
 * helper that returns one result object.
 *
 * allowed = clamp(k × dEff, vMin, vMax), dEff = min(target distance or the free distance, surfaceFactor × dSurface).
 * The ship's speed rises toward throttle × allowed through a first-order lag τ and never exceeds it.
 */
import { clamp } from '../../core/math'
import type { TravelConfig } from '../config'

/** The allowed cruise speed (m/s) for a target `dTarget` away and the nearest shell `dSurface` away. */
export function cruiseAllowed(dTarget: number, dSurface: number, hasTarget: boolean, cfg: TravelConfig): number {
  const c = cfg.cruise
  const dFree = hasTarget ? dTarget : c.freeDistanceM
  const dShell = c.surfaceFactor * dSurface
  const dEff = dFree < dShell ? dFree : dShell
  return clamp(c.k * dEff, c.vMin, c.vMax)
}

/** The allowed lane speed (m/s) with the exit ring `dExit` away: the cruise law with k × kMult and vMax × vMaxMult. */
export function lanesAllowed(dExit: number, cfg: TravelConfig): number {
  const c = cfg.cruise
  const l = cfg.lanes
  return clamp(c.k * l.kMult * dExit, c.vMin, c.vMax * l.vMaxMult)
}

/**
 * One step of the ship's cruise speed toward `target` (throttle × allowed). Rising, it is a first-order lag τ = `lagS`,
 * exact for any `dt` (a constant target over the step). Falling, the allowed speed is a cap and binds at once: a
 * symmetric lag would carry the ship above the cruise law as the target closes (the lagged law is underdamped for
 * 4τk > 1 and closes at rate 1/(2τ) instead of k), which breaks the 30–90 s trip rule.
 */
export function stepCruiseSpeed(speed: number, target: number, dt: number, lagS: number): number {
  if (target <= speed || lagS <= 0) return target
  if (dt <= 0) return speed
  return target + (speed - target) * Math.exp(-dt / lagS)
}

export interface TripResult {
  /** Seconds until the ship is within the drop distance (Infinity when it never gets there). */
  seconds: number
  /** The highest speed reached (m/s). */
  peak: number
}

/** The start speed of a simulated trip (m/s): a ship at combat speed engaging the drive. */
export const TRIP_START_SPEED = 200
/** Simulated trips give up after this many seconds. */
const TRIP_MAX_S = 3600

/**
 * A straight, aligned cruise at full throttle toward a target `distanceM` away, with no bodies, from 200 m/s until
 * the drop distance. Used to tune `k` and `lagS` against the 30–90 s trip rule (spec §4.2). `lagS` defaults to the
 * global `cruise.lagS`; a band's lag (`cruiseLagSFor`, M10 P5a7a) times that band's trips.
 */
export function simulateTrip(distanceM: number, cfg: TravelConfig, dt: number, lagS: number = cfg.cruise.lagS): TripResult {
  const c = cfg.cruise
  let d = distanceM
  let speed = TRIP_START_SPEED
  let peak = speed
  let t = 0
  while (d > c.dropDistanceM) {
    if (t >= TRIP_MAX_S) return { seconds: Infinity, peak }
    speed = stepCruiseSpeed(speed, cruiseAllowed(d, Infinity, true, cfg), dt, lagS)
    if (speed > peak) peak = speed
    d -= speed * dt
    t += dt
  }
  return { seconds: t, peak }
}
